import { ExternalLink, Gauge } from 'lucide-react';
import Notice from './Notice';
import ReportSection from './report/ReportSection';
import { tx } from '../i18n';
import { formatDate, hasLapsed } from '../lib/format';
import type { Quality, Status } from '../lib/status';
import type { EnergyCertificate, EnergyScale } from '../types/api';

interface EnergyCertificateSectionProps {
  certificate: EnergyCertificate;
  /** When the report read its sources, for the line at the foot of the section. */
  checkedAt: string;
}

/** The A-G scale, as the certificate defines it: the letter, its range, and how long its bar is. */
const BANDS = [
  { band: 'A', from: 92, to: 100, width: 46 },
  { band: 'B', from: 81, to: 91, width: 55 },
  { band: 'C', from: 69, to: 80, width: 64 },
  { band: 'D', from: 55, to: 68, width: 73 },
  { band: 'E', from: 39, to: 54, width: 82 },
  { band: 'F', from: 21, to: 38, width: 91 },
  { band: 'G', from: 1, to: 20, width: 100 },
] as const;

function range(from: number, to: number): string {
  return to === 100 ? `${from}+` : `${from}-${to}`;
}


/**
 * One rating scale drawn the way the certificate draws it.
 *
 * <p>The colours are the certificate's own, because a tenant who has seen an EPC recognises them
 * at a glance - but they are never the only encoding: every bar carries its letter and its range,
 * and each marker carries its number, so the chart reads the same in greyscale or with any colour
 * vision. The marker sits on the row its rating falls in, which is what makes the picture
 * meaningful: how far down the scale this home sits, and how far up it could go.
 */
function Scale({ scale, tone }: { scale: EnergyScale; tone: 'efficiency' | 'impact' }) {
  const current = scale.currentBand;
  const potential = scale.potentialBand;

  return (
    <div className="epc">
      <h3 className="label">{scale.label}</h3>
      <ol className={`epc__scale epc__scale--${tone}`}>
        {BANDS.map((entry) => (
          <li key={entry.band} className="epc__row">
            <span className={`epc__bar epc__bar--${entry.band.toLowerCase()}`} style={{ width: `${entry.width}%` }}>
              <span className="epc__range">{range(entry.from, entry.to)}</span>
              <span className="epc__letter">{entry.band}</span>
            </span>
            <span className="epc__markers">
              <span className="epc__slot">
                {entry.band === current && scale.currentPoints !== null ? (
                  <span className={`epc__marker epc__marker--${entry.band.toLowerCase()}`}>
                    {scale.currentPoints}
                  </span>
                ) : null}
              </span>
              <span className="epc__slot">
                {entry.band === potential && scale.potentialPoints !== null ? (
                  <span className="epc__marker epc__marker--potential">{scale.potentialPoints}</span>
                ) : null}
              </span>
            </span>
          </li>
        ))}
      </ol>
      <p className="tiny muted">{scale.summary}</p>
    </div>
  );
}

/**
 * The Energy Performance Certificate's own chart, drawn from the register's numbers rather than
 * copied as a picture, so it reads at any width and in either language.
 */
export default function EnergyCertificateSection({ certificate, checkedAt }: EnergyCertificateSectionProps) {
  const { efficiency, impact } = certificate;
  if (efficiency.currentPoints === null) {
    return null;
  }

  // A to C is what the sector treats as a decent home and what a landlord must reach to let one
  // after the planned uplift; D and E are lettable today but expensive; F and G cannot be let.
  const band = efficiency.currentBand ?? '';
  const bandStatus: Status = band === ''
    ? 'unknown'
    : 'ABC'.includes(band)
      ? 'good'
      : 'DE'.includes(band)
        ? 'attention'
        : 'risk';

  /*
   * Certificates last ten years, and the register prints the expiry date - which the section used
   * to pass on as a date and nothing more, leaving the reader to do the arithmetic. A tenant
   * reading "valid to 12 January 2026" in October 2026 is being told the certificate has lapsed
   * in a way that requires them to notice. Say it instead.
   */
  const expired = hasLapsed(certificate.expiresOn);

  /*
   * An expired certificate is never "good", whatever letter it carries: the letter describes the
   * home as it was measured, and the measurement is out of date. "Worth checking" is exactly what
   * the reader has to do. A band that was already attention or risk keeps its own worse verdict.
   */
  const status: Status = expired && bandStatus === 'good' ? 'attention' : bandStatus;

  /*
   * And it is not verified either. An exact address match says the certificate belongs to this
   * door; it says nothing about whether it still describes it.
   */
  const quality: Quality = expired || !certificate.exactAddress ? 'estimated' : 'verified';

  return (
    <ReportSection
      id="section-epc"
      icon={Gauge}
      title={tx('Energy Performance Certificate', 'Enerji Performans Sertifikası',
    'Świadectwo charakterystyki energetycznej',
    'Certificado de desempenho energético',
    'Certificado de eficiencia energética')}
      note={
        certificate.recordedOn
          ? tx(
              `Assessed ${formatDate(certificate.recordedOn)}${
                certificate.expiresOn ? `, valid to ${formatDate(certificate.expiresOn)}` : ''
              }.`,
              `Değerlendirme ${formatDate(certificate.recordedOn)}${
                certificate.expiresOn ? `, geçerlilik ${formatDate(certificate.expiresOn)}` : ''
              }.`,
              `Oceniono ${formatDate(certificate.recordedOn)}${
                certificate.expiresOn ? `, ważne do ${formatDate(certificate.expiresOn)}` : ''
              }.`,
              `Avaliado em ${formatDate(certificate.recordedOn)}${
                certificate.expiresOn ? `, válido até ${formatDate(certificate.expiresOn)}` : ''
              }.`,
              `Evaluado el ${formatDate(certificate.recordedOn)}${
                certificate.expiresOn ? `, válido hasta ${formatDate(certificate.expiresOn)}` : ''
              }.`,
            )
          : undefined
      }
      status={status}
      statusLabel={
        expired
          ? tx('Expired', 'Süresi dolmuş', 'Wygasło', 'Expirado', 'Caducado')
          : efficiency.currentBand
            ? tx(
                `Band ${efficiency.currentBand}`,
                `${efficiency.currentBand} bandı`,
                `Klasa ${efficiency.currentBand}`,
                `Classe ${efficiency.currentBand}`,
                `Letra ${efficiency.currentBand}`,
              )
            : undefined
      }
      quality={quality}
      source={{
        name: tx(
          'Energy Performance Certificate register',
          'Enerji Performans Sertifikası kaydı',
          'Rejestr świadectw charakterystyki energetycznej',
          'Registo de certificados energéticos',
          'Registro de certificados energéticos',
        ),
        published: certificate.recordedOn,
        checked: checkedAt,
      }}
      aside={
        efficiency.currentBand ? (
          <span className={`epc__badge epc__badge--${efficiency.currentBand.toLowerCase()}`}>
            {efficiency.currentBand}
          </span>
        ) : null
      }
    >
      {expired ? (
        <Notice
          tone="warning"
          title={tx(
            `This certificate expired on ${formatDate(certificate.expiresOn)}`,
            `Bu sertifikanın süresi ${formatDate(certificate.expiresOn)} tarihinde doldu`,
            `To świadectwo wygasło ${formatDate(certificate.expiresOn)}`,
            `Este certificado expirou em ${formatDate(certificate.expiresOn)}`,
            `Este certificado caducó el ${formatDate(certificate.expiresOn)}`,
          )}
        >
          <p>
            {tx(
              'The band and the running costs below are the last measurement anyone recorded, not a current one. Anything done to the home since - new windows, a new boiler, insulation - is not in them, and the cost estimate was modelled on the energy prices of the year it was assessed, so a real bill today will be higher.',
              'Aşağıdaki bant ve işletme maliyetleri, kayda geçmiş son ölçüm; bugünkü durum değil. O tarihten sonra yapılanlar — yeni pencere, yeni kombi, yalıtım — bunların içinde yok. Maliyet tahmini de değerlendirmenin yapıldığı yılın enerji fiyatlarıyla modellendi, yani bugünkü gerçek faturan daha yüksek olur.',
              'Klasa i koszty poniżej to ostatni zapisany pomiar, nie obecny. To, co zrobiono w domu później - nowe okna, nowy piec, izolacja - nie jest w nich uwzględnione, a szacunek kosztów opiera się na cenach energii z roku oceny, więc dzisiejszy rachunek będzie wyższy.',
              'A classe e os custos abaixo são a última medição registada, não a atual. O que foi feito na casa desde então - janelas novas, caldeira nova, isolamento - não está incluído, e a estimativa de custos foi modelada com os preços de energia do ano da avaliação, pelo que a fatura real hoje será mais alta.',
              'La letra y los costes de abajo son la última medición registrada, no la actual. Lo que se haya hecho en la vivienda desde entonces - ventanas nuevas, una caldera nueva, aislamiento - no está incluido, y la estimación de costes se modeló con los precios de la energía del año de la evaluación, así que la factura real hoy será más alta.',
            )}
          </p>
          <p>
            {tx(
              'In England a home may not normally be let without a valid certificate, so ask the landlord or agent whether a newer one has been lodged.',
              'İngiltere’de bir konut normalde geçerli bir sertifika olmadan kiraya verilemez; ev sahibine ya da emlakçıya daha yenisinin düzenlenip düzenlenmediğini sor.',
              'W Anglii mieszkania zwykle nie wolno wynająć bez ważnego świadectwa, więc zapytaj właściciela lub agenta, czy złożono nowsze.',
              'Em Inglaterra uma casa não pode normalmente ser arrendada sem certificado válido, por isso pergunte ao senhorio ou à agência se foi emitido um mais recente.',
              'En Inglaterra una vivienda no suele poder alquilarse sin un certificado válido, así que pregunta al propietario o a la agencia si se ha emitido uno más reciente.',
            )}
          </p>
        </Notice>
      ) : null}

      <div className="epc-grid">
        <Scale scale={efficiency} tone="efficiency" />
        {impact ? <Scale scale={impact} tone="impact" /> : null}
      </div>

      <p className="epc-legend tiny muted">
        <span className="epc-legend__item">
          <span className="epc__marker epc__marker--legend" aria-hidden="true" />
          {tx('Now', 'Şu an',
    'Teraz',
    'Agora',
    'Ahora')}
        </span>
        <span className="epc-legend__item">
          <span className="epc__marker epc__marker--potential epc__marker--legend" aria-hidden="true" />
          {tx('After the recommended works', 'Öneriler uygulanırsa',
    'Po zalecanych pracach',
    'Após as obras recomendadas',
    'Tras las obras recomendadas')}
        </span>
      </p>

      <p className="tiny muted">{certificate.note}</p>

      {certificate.certificateUrl ? (
        <p className="tiny">
          <a href={certificate.certificateUrl} target="_blank" rel="noreferrer">
            {tx('See the certificate on the government register', 'Sertifikanın aslını devlet kaydında görün',
    'Zobacz świadectwo w rejestrze państwowym',
    'Ver o certificado no registo oficial',
    'Ver el certificado en el registro oficial')}{' '}
            <ExternalLink size={11} aria-hidden="true" />
          </a>
        </p>
      ) : null}
    </ReportSection>
  );
}
